import { Capacitor } from '@capacitor/core';
import type { Contact } from '@hunting-nature/shared-core/interfaces/contact-response.interface';
import { createFeatureSelector, createReducer, on } from '@ngrx/store';

import { createScopedSelector } from '../../../shared/utils/factory.utils';

import { MenuActions } from './menu.action';
import type { SubscriptionPlan } from '@hunting-nature/shared-core/types/subscription-plan.type';
import { SubscriptionPlanValues } from '@hunting-nature/shared-core/types/subscription-plan.type';
import type { UserCreditsData } from '@hunting-nature/Secacam/shared/interfaces/user-credits-data.interface';
import type { UserSubscriptionData } from '@hunting-nature/Secacam/shared/interfaces/user-subscription-data.interface';

export const MENU_STORE_KEY = 'menu';
export const LOW_CREDIT_THRESHOLD = 25;

// state
export interface MenuState {
  freeDeviceSpace: number | undefined;
  calculatingFreeDeviceSpace: boolean;
  storageInformationShown: boolean;
  contacts: Array<Contact>;
  contactsLoading: boolean;
  subscription: UserSubscriptionData | undefined;
  credits: UserCreditsData | undefined;
}

export const initialGeneralState: MenuState = {
  freeDeviceSpace: undefined,
  calculatingFreeDeviceSpace: false,
  storageInformationShown: Capacitor.isNativePlatform(),
  contacts: [],
  contactsLoading: false,
  subscription: undefined,
  credits: undefined,
};

// selectors
const selectMenuState = createFeatureSelector<MenuState>(MENU_STORE_KEY);
const createMenuSelector = createScopedSelector(selectMenuState);

// reducers
export const menuReducer = createReducer(
  initialGeneralState,

  on(
    MenuActions.menuEntered,
    (state, { contactsAlreadyLoaded }): MenuState => ({
      ...state,
      calculatingFreeDeviceSpace: true,
      contactsLoading: !contactsAlreadyLoaded,
    })
  ),
  on(
    MenuActions.menuEnteredStorageSucceeded,
    (state, { freeSpace }): MenuState => ({
      ...state,
      freeDeviceSpace: freeSpace,
      calculatingFreeDeviceSpace: false,
    })
  ),
  on(MenuActions.menuEnteredStorageFailed, (state): MenuState => ({ ...state, calculatingFreeDeviceSpace: false })),
  on(
    MenuActions.menuEnteredContactsSucceeded,
    (state, { contacts }): MenuState => ({
      ...state,
      contacts,
      contactsLoading: false,
    })
  ),
  on(MenuActions.menuEnteredContactsFailed, (state): MenuState => ({ ...state, contactsLoading: false })),
  on(MenuActions.subscriptionCardEntered, state => ({
    ...state,
    loading: true,
  })),
  on(MenuActions.subscriptionCardEnterSucceeded, (state, { subscriptionDetails, creditDetails }) => ({
    ...state,
    loading: false,
    subscription: { ...subscriptionDetails, name: subscriptionDetails.name },
    credits: { ...creditDetails },
  })),
  on(MenuActions.subscriptionCardEnterFailed, (state, { error }) => ({
    ...state,
    loading: false,
    subscription: undefined,
  })),
  on(MenuActions.subscriptionsAndPlansPageEntered, state => ({
    ...state,
    loading: true,
  })),
  on(MenuActions.subscriptionsAndPlansPageEnteredSucceeded, (state, { subscriptionDetails, creditDetails }) => ({
    ...state,
    loading: false,
    subscription: { ...subscriptionDetails, name: subscriptionDetails.name },
    credits: { ...creditDetails },
  })),
  on(MenuActions.subscriptionsAndPlansPageEnteredFailed, (state, { error }) => ({
    ...state,
    loading: false,
    subscription: undefined,
  }))
);

export const MenuSelectors = {
  getFreeDeviceSpace: createMenuSelector((state: MenuState) => state.freeDeviceSpace),
  isCalculatingFreeDeviceSpace: createMenuSelector((state: MenuState) => state.calculatingFreeDeviceSpace),
  isStorageInformationShown: createMenuSelector((state: MenuState) => state.storageInformationShown),

  // Contacts selectors
  getContacts: createMenuSelector((state: MenuState) => state.contacts),
  isContactsLoading: createMenuSelector((state: MenuState) => state.contactsLoading),
  hasContacts: createMenuSelector((state: MenuState) => state.contacts.length > 0),

  subscriptionType: createMenuSelector((state: MenuState) => (state.subscription?.name as SubscriptionPlan) || SubscriptionPlanValues.NONE),
  expiresAt: createMenuSelector((state: MenuState) => state.subscription?.expiresAt),
  isPrepaid: createMenuSelector((state: MenuState) => (state.subscription?.name ?? SubscriptionPlanValues.NONE) === SubscriptionPlanValues.NONE),
  emptySlots: createMenuSelector((state: MenuState) => (state.subscription?.cameraSlots ?? 0) - (state.subscription?.assignedCamerasCount ?? 0)),
  prepaidCredits: createMenuSelector((state: MenuState) => state.credits?.paygCredits),
  videoCredits: createMenuSelector((state: MenuState) => state.subscription?.videoCredits.remaining),
  imageCredits: createMenuSelector((state: MenuState) => state.subscription?.imageCredits.remaining),
  unlimitedImageCredits: createMenuSelector((state: MenuState) => state.subscription?.imageCredits?.total === -1),
  cameraSlots: createMenuSelector((state: MenuState) => state.subscription?.cameraSlots),
  assignedCamerasCount: createMenuSelector((state: MenuState) => state.subscription?.assignedCamerasCount),
  emptyPrepaidCredits: createMenuSelector((state: MenuState) => state.credits?.paygCredits === 0),
  lowPrepaidCredits: createMenuSelector(
    (state: MenuState) => state.credits?.paygCredits !== undefined && (state.credits?.paygCredits ?? 0) < LOW_CREDIT_THRESHOLD
  ),
  lowVideoCredits: createMenuSelector(
    (state: MenuState) => state.subscription?.videoCredits?.remaining !== -1 && (state.subscription?.videoCredits?.remaining ?? 0) < LOW_CREDIT_THRESHOLD
  ),
  lowImageCredits: createMenuSelector(
    (state: MenuState) => state.subscription?.imageCredits?.remaining !== -1 && (state.subscription?.imageCredits?.remaining ?? 0) < LOW_CREDIT_THRESHOLD
  ),
};
